'use client';

import { Loader2 } from 'lucide-react';

import type { QuizQuestion, TaskFile } from '@/entities/interview-task';
import { CodeEditor, CodeViewer } from '@/shared/ui/code-editor';
import { INTERVIEW_THEME_NAME } from '@/shared/ui/code-editor/config';
import { TheoryMarkdown } from '@/shared/ui/markdown';

import { InlineQuiz } from './inline-quiz';
import { PanelHeader } from './panel-header';
import {
	SidePanelFooter,
	type SidePanelFooterProps,
} from './side-panel-footer';

export type OpenedQuiz = { question: QuizQuestion; key: string };

type SidePanelProps = {
	openedFile: TaskFile | null;
	openedQuiz: OpenedQuiz | null;
	pinnedFiles: TaskFile[];
	onSelectFile: (file: TaskFile) => void;
	onClose: () => void;
	getEditorDraft: (file: TaskFile) => string;
	setEditorDraft: (fileName: string, value: string) => void;
	footer: SidePanelFooterProps | null;
	quizIsLocked: boolean;
	/** Действия (Проверить/Далее/Прочитал/Отправить) доступны только в idle. */
	actionsDisabled?: boolean;
	/** Пока true — анимация открытия панели ещё идёт, тяжёлый контент не маунтим. */
	isOpening?: boolean;
	onQuizAttempt: (answer: string) => void;
	onQuizCorrect: (
		answer: string,
		ctx: { optionIds: string[]; answerText: string },
	) => void;
	onQuizWrong: (hint: string) => void;
};

export function SidePanel({
	openedFile,
	openedQuiz,
	pinnedFiles,
	onSelectFile,
	onClose,
	getEditorDraft,
	setEditorDraft,
	footer,
	quizIsLocked,
	actionsDisabled = false,
	isOpening = false,
	onQuizAttempt,
	onQuizCorrect,
	onQuizWrong,
}: SidePanelProps) {
	if (!openedFile && !openedQuiz) return null;

	return (
		<div className="flex h-full min-w-0 flex-1 flex-col border-l border-gray-4 bg-white">
			<PanelHeader
				mode={openedQuiz ? 'quiz' : 'file'}
				openedFile={openedFile}
				pinnedFiles={pinnedFiles}
				onSelectFile={onSelectFile}
				onClose={onClose}
			/>

			<div className="flex-1 overflow-auto">
				{isOpening ? (
					<div className="flex h-full items-center justify-center">
						<Loader2
							size={28}
							strokeWidth={2}
							className="animate-spin text-gray-3"
						/>
					</div>
				) : openedQuiz ? (
					<InlineQuiz
						key={openedQuiz.key}
						question={openedQuiz.question}
						isLocked={quizIsLocked}
						actionsDisabled={actionsDisabled}
						onAttempt={onQuizAttempt}
						onCorrect={onQuizCorrect}
						onWrong={onQuizWrong}
					/>
				) : openedFile?.kind === 'theory' || openedFile?.kind === 'markdown' ? (
					<div className="mx-auto max-w-[642px] px-8 py-7">
						<TheoryMarkdown
							variant={openedFile.name === 'Report.md' ? 'report' : 'default'}
						>
							{openedFile.content}
						</TheoryMarkdown>
					</div>
				) : openedFile?.kind === 'view' ? (
					<CodeViewer
						code={openedFile.code}
						lang={openedFile.lang}
						showLineNumbers
						theme={INTERVIEW_THEME_NAME}
					/>
				) : openedFile?.kind === 'edit' ? (
					<CodeEditor
						value={getEditorDraft(openedFile)}
						onChange={(value) => setEditorDraft(openedFile.name, value)}
						language={openedFile.lang}
						autoComplete={false}
						maxLength={20000}
						theme={INTERVIEW_THEME_NAME}
					/>
				) : null}
			</div>

			{footer && <SidePanelFooter {...footer} disabled={actionsDisabled} />}
		</div>
	);
}
